import { differenceInCalendarDays, parseISO } from "date-fns";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useSceneId } from "hooks/useSceneId";
import { telemetry } from "telemetry";

import { offlineActions, selectOfflineScenes } from "./offlineSlice";
import { useOfflineSceneDirName } from "./useOfflineSceneKey";

type SceneHistory = {
    visits: string[];
    incrementalTriggered: boolean;
};

const emptyHistory: SceneHistory = {
    visits: [],
    incrementalTriggered: false,
};
const visitThreshold = 3;
const periodDays = 30;
let handled = false;

export function useHandleAutoIncremental() {
    const {
        state: { offlineWorkerState },
    } = useExplorerGlobals();

    const sceneId = useSceneId();
    const viewerSceneId = useOfflineSceneDirName();
    const currentScene = useAppSelector(selectOfflineScenes)[viewerSceneId];
    const currentViewerScene = currentScene?.viewerScenes.find((scene) => scene.id === viewerSceneId);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (handled || !offlineWorkerState || currentViewerScene) {
            return;
        }

        handled = true;
        const history = getSceneHistory(sceneId);

        if (history.incrementalTriggered) {
            return;
        }

        const today = new Date();
        history.visits = history.visits.filter((visit) => {
            return differenceInCalendarDays(today, parseISO(visit)) <= periodDays;
        });
        history.visits.push(today.toISOString());

        if (history.visits.length >= visitThreshold) {
            history.incrementalTriggered = true;
            dispatch(offlineActions.setAction({ action: "incrementalSync" }));
        }

        localStorage.setItem(getStorageKey(sceneId), JSON.stringify(history));
    }, [sceneId, currentViewerScene, offlineWorkerState, dispatch]);
}

function getStorageKey(sceneId: string) {
    return `auto_incremental_loading/${sceneId}`;
}

function getSceneHistory(sceneId: string): SceneHistory {
    const jsonStr = localStorage.getItem(getStorageKey(sceneId));

    if (!jsonStr) {
        return emptyHistory;
    }

    try {
        return JSON.parse(jsonStr) as SceneHistory;
    } catch (e) {
        console.warn(e);
        telemetry.trackCaughtException({
            source: "offline.useHandleAutoIncremental.getSceneHistory",
            category: "data",
            error: e,
        });

        return emptyHistory;
    }
}
